<template>
  <div class="lottery-management-container">
    <!-- Left Panel: Draw Management -->
    <div class="left-panel">
      <div class="panel-header">
        <h3>开奖管理</h3>
      </div>

      <!-- Lottery Type Selector -->
      <div class="lottery-selector">
        <span class="selector-label">选择彩种:</span>
        <el-select v-model="selectedType" @change="onTypeChange" style="width: 100%">
          <el-option v-for="lottery in lotteries" :key="lottery.type" :label="lottery.lottery_name"
            :value="lottery.type" />
        </el-select>
      </div>

      <div class="draw-controls">
        <!-- Current period info with countdown -->
        <el-card class="period-info" :class="{ 'disabled-lottery': currentLottery?.status == 0 }">
          <h4>第{{ currentLottery?.next_period }}期</h4>
          <p class="next-draw-label">下期开奖: {{ currentLottery?.next_draw_time }}</p>
          <div v-if="currentLottery?.status == 1" class="countdown-display">
            <div class="countdown-item">
              <span class="countdown-value">{{ leftCountdown.hours }}</span>
              <span class="countdown-label">时</span>
            </div>
            <div class="countdown-separator">:</div>
            <div class="countdown-item">
              <span class="countdown-value">{{ leftCountdown.minutes }}</span>
              <span class="countdown-label">分</span>
            </div>
            <div class="countdown-separator">:</div>
            <div class="countdown-item">
              <span class="countdown-value">{{ leftCountdown.seconds }}</span>
              <span class="countdown-label">秒</span>
            </div>
          </div>
          <div v-else class="countdown-paused-large">已暂停</div>
        </el-card>

        <!-- Draw method selection -->
        <el-card class="draw-method-card">
          <h4>开奖方式</h4>
          <el-radio-group v-model="drawMethod" class="method-group">
            <el-radio label="random">随机开奖</el-radio>
            <el-radio label="manual">手动开奖</el-radio>
          </el-radio-group>
        </el-card>

        <!-- Manual input -->
        <el-card v-if="drawMethod === 'manual'" class="manual-input-card">
          <h4>输入7个号码 (1-49)</h4>
          <div class="manual-inputs">
            <div v-for="i in 6" :key="i" class="ball-input-wrapper">
              <label class="ball-label">球{{ i }}</label>
              <el-input-number v-model="manualBalls[i - 1]" :min="1" :max="49" size="small" />
            </div>
          </div>
          <div class="special-ball-input">
            <label class="ball-label special-label">特别号</label>
            <el-input-number v-model="manualBalls[6]" :min="1" :max="49" size="small" />
          </div>
        </el-card>

        <el-button type="primary" size="large" @click="executeDraw" :loading="drawLoading" class="execute-button">
          执行开奖
        </el-button>
      </div>
    </div>

    <!-- Right Panel: Both Lottery Results -->
    <div class="right-panel">
      <div class="panel-header">
        <h3>最新开奖结果</h3>
      </div>

      <div class="lottery-results-grid">
        <div v-for="lottery in lotteries" :key="lottery.type" class="lottery-result-card"
          :class="{ 'active': lottery.type === selectedType, 'macau': lottery.type === 0, 'hongkong': lottery.type === 1 }">
          <div class="result-header">
            <div class="lottery-title">
              <h3>{{ lottery.lottery_name }}</h3>
              <el-tag size="small" :type="lottery.status == 1 ? 'success' : 'danger'">
                {{ lottery.status == 1 ? '启用' : '禁用' }}
              </el-tag>
            </div>
            <div class="period-info-small">
              <span>第{{ lottery.last_draw?.period }}期</span>
              <span class="draw-time">{{ lottery.last_draw?.draw_time }}</span>
            </div>
          </div>

          <!-- Balls with zodiac/element info -->
          <div v-if="lottery.last_draw" class="balls-container">
            <div class="regular-balls">
              <div v-for="(ball, index) in lottery.last_draw.balls.slice(0, 6)" :key="index" class="ball-wrapper">
                <div :class="['ball', `ball-${ball.color}`]">{{ ball.number }}</div>
                <div class="ball-info">{{ ball.zodiac }}/{{ ball.element }}</div>
              </div>
            </div>
            <div class="plus-sign">+</div>
            <div class="ball-wrapper special">
              <div :class="['ball', 'special-ball', `ball-${lottery.last_draw.balls[6]?.color}`]">
                {{ lottery.last_draw.balls[6]?.number }}
              </div>
              <div class="ball-info">{{ lottery.last_draw.balls[6]?.zodiac }}/{{ lottery.last_draw.balls[6]?.element }}
              </div>
            </div>
          </div>
          <el-empty v-else description="暂无开奖记录" :image-size="60" />

          <!-- Countdown for this lottery -->
          <div class="lottery-countdown">
            <span>下期倒计时:</span>
            <span v-if="lottery.status == 1" class="countdown-text">{{ lottery.countdown_display || '--:--:--' }}</span>
            <span v-else class="countdown-paused">已暂停</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, getCurrentInstance, onMounted, onUnmounted } from "vue";
import { ElMessage } from "element-plus";

const { proxy } = getCurrentInstance();

const lotteries = ref([]);
const selectedType = ref(0);
const drawMethod = ref("random");
const manualBalls = ref([1, 2, 3, 4, 5, 6, 7]);
const drawLoading = ref(false);
let pollInterval = null;
let countdownInterval = null;

// Reactive countdown for left panel (updated every second)
const leftCountdown = ref({ hours: '00', minutes: '00', seconds: '00' });

// Current selected lottery
const currentLottery = computed(() => {
  return lotteries.value.find(l => l.type === selectedType.value) || {};
});

// Format countdown for each lottery
const formatCountdown = (nextDrawTime) => {
  if (!nextDrawTime) return '--:--:--';
  const diff = new Date(nextDrawTime).getTime() - Date.now();
  if (diff <= 0) return '开奖中...';
  const h = Math.floor(diff / 1000 / 3600);
  const m = Math.floor((diff / 1000 % 3600) / 60);
  const s = Math.floor(diff / 1000 % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

// Update countdowns every second
const updateCountdowns = () => {
  // Update right panel countdowns
  lotteries.value.forEach(lottery => {
    lottery.countdown_display = formatCountdown(lottery.next_draw_time);
  });

  // Update left panel countdown (for selected lottery)
  const lottery = lotteries.value.find(l => l.type === selectedType.value);
  if (lottery?.next_draw_time) {
    const diff = new Date(lottery.next_draw_time).getTime() - Date.now();
    if (diff <= 0) {
      leftCountdown.value = { hours: '00', minutes: '00', seconds: '00' };
    } else {
      const hours = Math.floor(diff / 1000 / 3600);
      const minutes = Math.floor((diff / 1000 % 3600) / 60);
      const seconds = Math.floor(diff / 1000 % 60);
      leftCountdown.value = {
        hours: String(hours).padStart(2, '0'),
        minutes: String(minutes).padStart(2, '0'),
        seconds: String(seconds).padStart(2, '0')
      };
    }
  } else {
    leftCountdown.value = { hours: '00', minutes: '00', seconds: '00' };
  }
};

// Get all dashboard data
const getAllDashboard = async () => {
  try {
    const res = await proxy.$http({
      type: "get",
      url: "/LotteryDraw/getAllDashboard"
    });
    if (res.code == 0) {
      // Sort by type: Macau (0) first, Hong Kong (1) second
      lotteries.value = (res.data?.lotteries || []).sort((a, b) => a.type - b.type);
      updateCountdowns();
    }
  } catch (error) {
    console.error("Failed to get dashboard:", error);
  }
};

// Change lottery type
const onTypeChange = () => {
  updateCountdowns();
};

// Execute draw
const executeDraw = async () => {
  if (drawMethod.value === "manual") {
    const uniqueBalls = [...new Set(manualBalls.value)];
    if (uniqueBalls.length !== 7) {
      ElMessage.warning("请输入7个不重复的号码！");
      return;
    }
  }

  drawLoading.value = true;
  try {
    const params = {
      type: selectedType.value,
      method: drawMethod.value
    };
    if (drawMethod.value === "manual") {
      params.balls = [...manualBalls.value];
    }

    const res = await proxy.$http({
      method: "post",
      url: "/LotteryDraw/executeDraw",
      data: params
    });

    if (res.code == 0) {
      ElMessage.success("开奖执行成功！");
      getAllDashboard();
    } else {
      ElMessage.error(res.msg || "开奖失败");
    }
  } catch (error) {
    ElMessage.error("开奖执行失败");
  } finally {
    drawLoading.value = false;
  }
};

onMounted(() => {
  getAllDashboard();
  pollInterval = setInterval(getAllDashboard, 5000);
  countdownInterval = setInterval(updateCountdowns, 1000);
});

onUnmounted(() => {
  if (pollInterval) clearInterval(pollInterval);
  if (countdownInterval) clearInterval(countdownInterval);
});
</script>

<style lang="less" scoped>
.lottery-management-container {
  display: flex;
  height: calc(100vh - 120px);
  gap: 20px;
}

.left-panel {
  width: 320px;
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow-y: auto;
}

.right-panel {
  flex: 1;
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow-y: auto;
}

.panel-header {
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px solid #f0f0f0;

  h3 {
    margin: 0;
    font-size: 18px;
    color: #333;
  }
}

.lottery-selector {
  margin-bottom: 20px;

  .selector-label {
    display: block;
    font-size: 13px;
    color: #666;
    margin-bottom: 8px;
  }
}

.draw-controls {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.period-info {
  text-align: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;

  h4 {
    margin: 0 0 8px 0;
    color: white;
    font-size: 18px;
  }

  .next-draw-label {
    margin: 0 0 12px 0;
    color: rgba(255, 255, 255, 0.9);
    font-size: 12px;
  }

  .countdown-display {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .countdown-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    padding: 10px 15px;
  }

  .countdown-value {
    font-size: 20px;
    font-weight: bold;
    color: white;
  }

  .countdown-label {
    font-size: 10px;
    color: rgba(255, 255, 255, 0.8);
    margin-top: 4px;
  }

  .countdown-separator {
    font-size: 20px;
    font-weight: bold;
    color: white;
    margin: 0 5px;
  }
}

.draw-method-card {
  h4 {
    margin: 0 0 15px 0;
    color: #333;
    font-size: 14px;
  }

  .method-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
}

.manual-input-card {
  h4 {
    margin: 0 0 15px 0;
    color: #333;
    font-size: 14px;
  }

  .manual-inputs {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    margin-bottom: 15px;
  }

  .ball-input-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
  }

  .ball-label {
    font-size: 11px;
    color: #666;
  }

  .special-label {
    color: #e6a23c !important;
    font-weight: bold;
  }

  .special-ball-input {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
    padding: 10px;
    background: #fff8e6;
    border-radius: 8px;
    border: 2px dashed #e6a23c;
  }
}

.execute-button {
  height: 45px;
  font-size: 15px;
  font-weight: bold;
}

.lottery-results-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 20px;
}

.lottery-result-card {
  background: #fafafa;
  border-radius: 12px;
  padding: 20px;
  border: 2px solid #eee;
  transition: all 0.3s;

  &.macau {
    border-color: #667eea;
    background: #f8f9ff;
  }

  &.hongkong {
    border-color: #67c23a;
    background: #f0f9eb;
  }

  &.active {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
}

.result-header {
  margin-bottom: 15px;

  .lottery-title {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 5px;

    h3 {
      margin: 0;
      font-size: 16px;
      color: #333;
    }
  }

  .period-info-small {
    display: flex;
    gap: 15px;
    font-size: 13px;
    color: #666;

    .draw-time {
      color: #999;
    }
  }
}

.balls-container {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 15px;
  flex-wrap: wrap;
}

.regular-balls {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.ball-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;

  &.special .ball {
    border: 3px solid #ffd700 !important;
    box-shadow: 0 0 8px rgba(255, 215, 0, 0.5);
  }
}

.ball {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: bold;
  color: white;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
}

.ball-red {
  background: linear-gradient(135deg, #ff4757, #ff3742);
  border: 2px solid #ff2d3a;
}

.ball-blue {
  background: linear-gradient(135deg, #3742fa, #2f3542);
  border: 2px solid #2632eb;
}

.ball-green {
  background: linear-gradient(135deg, #2ed573, #1dd1a1);
  border: 2px solid #0abf53;
}

.ball-info {
  font-size: 10px;
  color: #666;
  text-align: center;
  white-space: nowrap;
}

.plus-sign {
  font-size: 18px;
  font-weight: bold;
  color: #666;
  margin: 10px 5px 0;
}

.lottery-countdown {
  display: flex;
  align-items: center;
  gap: 10px;
  padding-top: 15px;
  border-top: 1px solid #eee;
  font-size: 13px;
  color: #666;

  .countdown-text {
    font-family: monospace;
    font-size: 16px;
    font-weight: bold;
    color: #409eff;
  }

  .countdown-paused {
    font-size: 14px;
    color: #909399;
    font-style: italic;
  }
}

.countdown-paused-large {
  font-size: 18px;
  color: rgba(255, 255, 255, 0.7);
  font-style: italic;
  padding: 20px 0;
}

.disabled-lottery {
  opacity: 0.7;
  background: linear-gradient(135deg, #909399 0%, #606266 100%) !important;
}

@media (max-width: 1200px) {
  .lottery-management-container {
    flex-direction: column;
    height: auto;
  }

  .left-panel {
    width: 100%;
  }

  .lottery-results-grid {
    grid-template-columns: 1fr;
  }
}
</style>
